import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { callGoogleAdsProxy, GOOGLE_ADS_API_VERSION_PATH } from "../services/google-ads-proxy-client";

import { useGoogleAdsConnection } from "./use-google-ads-oauth";

export interface IGoogleAdsAccountOption {
    id: string;
    name: string;
    loginCustomerId: string;
}

interface IGoogleAdsAccessibleCustomersResponse {
    resourceNames?: string[];
}

interface IGoogleAdsCustomerClientResult {
    customerClient?: {
        id?: string;
        descriptiveName?: string;
        manager?: boolean;
        status?: string;
        testAccount?: boolean;
    };
}

interface IGoogleAdsCustomerClientResponse {
    results?: IGoogleAdsCustomerClientResult[];
}

const CUSTOMER_CLIENT_QUERY =
    "SELECT customer_client.id, customer_client.descriptive_name, customer_client.manager, customer_client.status, customer_client.test_account FROM customer_client";

const NON_SELECTABLE_STATUSES = new Set(["CANCELED", "SUSPENDED", "CLOSED"]);

// `listAccessibleCustomers` only returns the top-level customers tied to a login. If one of
// those is a manager (MCC) account, its real, servable ad accounts are its children in the
// `customer_client` hierarchy, not the manager itself — so each top-level customer is expanded
// here into its non-manager, enabled descendants.
const resolveAccountsUnderTopLevelCustomer = async (topLevelCustomerId: string): Promise<IGoogleAdsAccountOption[]> => {
    const response = await callGoogleAdsProxy<IGoogleAdsCustomerClientResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${topLevelCustomerId}/googleAds:search`,
        body: { query: CUSTOMER_CLIENT_QUERY },
        loginCustomerId: topLevelCustomerId,
    });

    return (response.results ?? [])
        .map((result) => result.customerClient)
        .filter(
            (
                client,
            ): client is {
                id: string;
                descriptiveName?: string;
                manager?: boolean;
                status?: string;
                testAccount?: boolean;
            } =>
                Boolean(client?.id) &&
                client?.manager !== true &&
                // Google Ads test accounts always report status CLOSED (no billing) — bypass the
                // status check for them so real cancelled accounts stay excluded but test accounts don't.
                (client?.testAccount === true || !NON_SELECTABLE_STATUSES.has(client?.status ?? "")),
        )
        .map((client) => ({
            id: client.id,
            name: client.descriptiveName || `Account ${client.id}`,
            loginCustomerId: topLevelCustomerId,
        }));
};

const fetchGoogleAdsAccounts = async (): Promise<IGoogleAdsAccountOption[]> => {
    const accessibleCustomers = await callGoogleAdsProxy<IGoogleAdsAccessibleCustomersResponse>({
        method: "GET",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers:listAccessibleCustomers`,
    });

    const topLevelCustomerIds = (accessibleCustomers.resourceNames ?? []).map((resourceName) =>
        resourceName.replace("customers/", ""),
    );
    if (topLevelCustomerIds.length === 0) {
        throw new Error("The connected Google account has no accessible Google Ads customers.");
    }

    const settledLookups = await Promise.allSettled(topLevelCustomerIds.map(resolveAccountsUnderTopLevelCustomer));

    const accountsByTopLevel: IGoogleAdsAccountOption[][] = [];
    const failures: unknown[] = [];
    settledLookups.forEach((lookup, index) => {
        if (lookup.status === "fulfilled") {
            accountsByTopLevel.push(lookup.value);
        } else {
            failures.push(lookup.reason);
            console.warn(
                `[Google Ads] failed to resolve account hierarchy for ${topLevelCustomerIds[index]}:`,
                lookup.reason,
            );
        }
    });

    // Partial failures keep the accounts that did resolve; only when every lookup failed is an empty
    // list misleading (expired auth, rate limit, outage), so surface the error and let the UI retry.
    if (accountsByTopLevel.length === 0) {
        throw failures[0];
    }

    const dedupedById = new Map<string, IGoogleAdsAccountOption>();
    for (const account of accountsByTopLevel.flat()) {
        if (!dedupedById.has(account.id)) {
            dedupedById.set(account.id, account);
        }
    }

    return [...dedupedById.values()];
};

export const useGoogleAdsAccounts = (): UseQueryResult<IGoogleAdsAccountOption[]> => {
    const { isConnected, connectionId } = useGoogleAdsConnection();

    return useQuery({
        queryKey: ["googleAdsAccounts", connectionId],
        queryFn: fetchGoogleAdsAccounts,
        enabled: isConnected,
        retry: false,
    });
};
